import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PhotoCropDialog } from "@/components/PhotoCropDialog";
import { Loader2, Plus, Pencil, Trash2, BookOpen, Image as ImageIcon, GripVertical } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const empty = { title: "", category_slug: "", abbreviation: "", issn: "", description: "", scope: "", cover_image: "", impact_factor: "", frequency: "Quarterly", editor_in_chief: "", eic_designation: "", eic_department: "", eic_affiliation: "", eic_email: "", eic_bio: "", eic_homepage: "", eic_photo: "", apc: "", editorial_board: [] };

const BOARD_ROLES = ["Associate Editor", "Editorial Board Member", "Guest Editor", "Expert Reviewer", "Advisory Board", "Section Editor", "Managing Editor"];

function JournalForm({ initial, categories, onSave }) {
  const [f, setF] = useState(initial || empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title || !f.category_slug) { toast.error("Title and category are required."); return; }
    setBusy(true);
    try {
      await onSave({
        ...f,
        indexing: f.indexing || ["Google Scholar", "Crossref"],
        editorial_board: (f.editorial_board || []).filter((m) => (m.name || "").trim()),
      });
    } finally { setBusy(false); }
  };

  const board = f.editorial_board || [];
  const setBoard = (b) => setF({ ...f, editorial_board: b });
  const setMember = (i, k, v) => setBoard(board.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const moveMember = (from, to) => {
    if (from === null || from === to) return;
    const b = [...board];
    const [moved] = b.splice(from, 1);
    b.splice(to, 0, moved);
    setBoard(b);
  };
  const [dragIdx, setDragIdx] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(-1);
  const [cropJob, setCropJob] = useState(null);
  const pickPhoto = (target, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setCropJob({ target, src: URL.createObjectURL(file) });
  };
  const closeCrop = () => { if (cropJob) URL.revokeObjectURL(cropJob.src); setCropJob(null); };
  const uploadCropped = async (blob) => {
    const target = cropJob.target;
    setPhotoBusy(target);
    try {
      const fd = new FormData();
      fd.append("file", blob, "photo.jpg");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "eic") setF((prev) => ({ ...prev, eic_photo: data.url }));
      else setBoard(board.map((m, idx) => (idx === target ? { ...m, photo: data.url } : m)));
      toast.success("Photo ready. Save the journal to publish it.");
      closeCrop();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setPhotoBusy(-1); }
  };
  const uploadMemberPhoto = (i, e) => pickPhoto(i, e);
  const uploadEicPhoto = (e) => pickPhoto("eic", e);

  return (
    <div className="space-y-3">
      <PhotoCropDialog src={cropJob?.src} onCancel={closeCrop} onConfirm={uploadCropped} />
      <div><Label>Title *</Label><Input data-testid="journal-title-input" value={f.title} onChange={set("title")} className="mt-1" /></div>
      <div><Label>Category *</Label>
        <Select value={f.category_slug} onValueChange={(v) => setF({ ...f, category_slug: v })}>
          <SelectTrigger data-testid="journal-category-select" className="mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Abbreviation</Label><Input value={f.abbreviation} onChange={set("abbreviation")} className="mt-1" /></div>
        <div><Label>ISSN</Label><Input value={f.issn} onChange={set("issn")} className="mt-1" /></div>
        <div><Label>Impact Factor</Label><Input value={f.impact_factor} onChange={set("impact_factor")} className="mt-1" /></div>
        <div><Label>Frequency</Label><Input value={f.frequency} onChange={set("frequency")} className="mt-1" /></div>
        <div><Label>Editor-in-Chief</Label><Input value={f.editor_in_chief} onChange={set("editor_in_chief")} className="mt-1" /></div>
        <div><Label>APC</Label><Input value={f.apc} onChange={set("apc")} className="mt-1" /></div>
      </div>
      <div><Label>Cover image URL</Label><Input value={f.cover_image} onChange={set("cover_image")} className="mt-1" /></div>
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/40 dark:bg-amber-950/10 p-3 space-y-2">
        <Label className="text-amber-700 dark:text-amber-400">Editor-in-Chief details</Label>
        <div className="flex items-center gap-3">
          <label className="w-16 h-16 rounded-full shrink-0 grid place-items-center overflow-hidden bg-white dark:bg-slate-800 cursor-pointer border border-amber-200 dark:border-amber-800" title="Upload Editor-in-Chief photo" data-testid="eic-photo-upload">
            {photoBusy === "eic" ? <Loader2 className="w-5 h-5 animate-spin" /> : f.eic_photo ? <img src={`${BACKEND}${f.eic_photo}`} className="w-full h-full object-cover" alt="Editor-in-Chief" data-testid="eic-photo-preview" /> : <ImageIcon className="w-5 h-5 text-slate-400" />}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadEicPhoto} data-testid="eic-photo-input" />
          </label>
          <div className="text-xs text-slate-500 space-y-1">
            <p>Click the circle to {f.eic_photo ? "replace" : "upload"} the Editor-in-Chief photo (JPG, PNG or WebP).</p>
            {f.eic_photo && <button type="button" className="text-red-600 hover:underline" onClick={() => setF({ ...f, eic_photo: "" })} data-testid="eic-photo-remove">Remove photo</button>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input value={f.eic_designation || ""} onChange={set("eic_designation")} placeholder="Designation (e.g. Professor)" className="h-8" data-testid="eic-designation" />
          <Input value={f.eic_department || ""} onChange={set("eic_department")} placeholder="Department" className="h-8" data-testid="eic-department" />
        </div>
        <Input value={f.eic_affiliation || ""} onChange={set("eic_affiliation")} placeholder="Institution & full address" className="h-8" data-testid="eic-affiliation" />
        <Input type="email" value={f.eic_email || ""} onChange={set("eic_email")} placeholder="Official / institutional email" className="h-8" data-testid="eic-email" />
        <Input type="url" value={f.eic_homepage || ""} onChange={set("eic_homepage")} placeholder="Home page / personal website URL" className="h-8" data-testid="eic-homepage" />
        <Textarea value={f.eic_bio || ""} onChange={set("eic_bio")} placeholder="Short biography (shown on the Editor-in-Chief profile page)" rows={2} className="text-sm" data-testid="eic-bio" />
      </div>
      <div><Label>Description / Scope</Label><Textarea value={f.scope || f.description} onChange={(e) => setF({ ...f, scope: e.target.value, description: e.target.value })} rows={3} className="mt-1" /></div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label>Editorial Board</Label>
          <Button type="button" variant="outline" size="sm" className="rounded-full gap-1 h-7" onClick={() => setBoard([...board, { name: "", role: "Editorial Board Member", affiliation: "" }])} data-testid="add-board-member"><Plus className="w-3.5 h-3.5" /> Add</Button>
        </div>
        {board.length === 0 && <p className="text-xs text-slate-400">No board members yet. The Editor-in-Chief is set above.</p>}
        <p className="text-xs text-slate-400 mb-2">Drag the <GripVertical className="w-3 h-3 inline" /> handle to reorder members.</p>
        <div className="space-y-2">
          {board.map((m, i) => (
            <div key={i}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { moveMember(dragIdx, i); setDragIdx(null); }}
              onDragEnd={() => setDragIdx(null)}
              className={`border rounded-lg p-2.5 space-y-2 transition-opacity ${dragIdx === i ? "opacity-40 border-primary" : "border-slate-200 dark:border-slate-800"}`}
              data-testid={`board-member-${i}`}>
              <div className="flex items-center gap-2">
                <span className="cursor-grab active:cursor-grabbing text-slate-400 shrink-0" data-testid={`board-drag-${i}`}><GripVertical className="w-4 h-4" /></span>
                <label className="w-9 h-9 rounded-full shrink-0 grid place-items-center overflow-hidden bg-slate-100 dark:bg-slate-800 cursor-pointer border border-slate-200 dark:border-slate-700" title="Upload photo" data-testid={`board-photo-${i}`}>
                  {photoBusy === i ? <Loader2 className="w-4 h-4 animate-spin" /> : m.photo ? <img src={`${BACKEND}${m.photo}`} className="w-full h-full object-cover" alt="" /> : <ImageIcon className="w-4 h-4 text-slate-400" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMemberPhoto(i, e)} />
                </label>
                {m.photo && <button type="button" title="Remove photo" className="text-[10px] text-red-600 hover:underline shrink-0" onClick={() => setMember(i, "photo", "")} data-testid={`board-photo-remove-${i}`}>Remove</button>}
                <Input value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} placeholder="Name" className="h-8" data-testid={`board-name-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setBoard(board.filter((_, idx) => idx !== i))} data-testid={`board-remove-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
              <div className="flex gap-2">
                <Select value={m.role || "Editorial Board Member"} onValueChange={(v) => setMember(i, "role", v)}>
                  <SelectTrigger className="h-8 w-[185px] shrink-0 text-sm" data-testid={`board-role-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{BOARD_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={m.designation || ""} onChange={(e) => setMember(i, "designation", e.target.value)} placeholder="Designation (e.g. Professor)" className="h-8" data-testid={`board-designation-${i}`} />
                <Input value={m.department || ""} onChange={(e) => setMember(i, "department", e.target.value)} placeholder="Department" className="h-8" data-testid={`board-department-${i}`} />
              </div>
              <div className="flex gap-2">
                <Input value={m.affiliation || ""} onChange={(e) => setMember(i, "affiliation", e.target.value)} placeholder="Institution & full address" className="h-8" data-testid={`board-affiliation-${i}`} />
                <Input type="email" value={m.email || ""} onChange={(e) => setMember(i, "email", e.target.value)} placeholder="Official / institutional email" className="h-8" data-testid={`board-email-${i}`} />
              </div>
              <Input type="url" value={m.homepage || ""} onChange={(e) => setMember(i, "homepage", e.target.value)} placeholder="Home page / personal website URL" className="h-8" data-testid={`board-homepage-${i}`} />
              <Textarea value={m.bio || ""} onChange={(e) => setMember(i, "bio", e.target.value)} placeholder="Short biography (shown on the member's profile page)" rows={2} className="text-sm" data-testid={`board-bio-${i}`} />
            </div>
          ))}
        </div>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={busy} data-testid="journal-save-btn" className="rounded-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Journal"}</Button>
      </DialogFooter>
    </div>
  );
}

function JournalsTab({ categories }) {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/journals").then(({ data }) => setJournals(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing) await api.put(`/journals/${editing.slug}`, data);
      else await api.post("/journals", data);
      toast.success("Journal saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this journal?")) return;
    await api.delete(`/journals/${slug}`); toast.success("Deleted"); load();
  };

  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button onClick={() => setEditing(null)} className="rounded-full gap-1.5" data-testid="add-journal-btn"><Plus className="w-4 h-4" /> Add Journal</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif">{editing ? "Edit Journal" : "New Journal"}</DialogTitle></DialogHeader>
            <JournalForm initial={editing} categories={categories} onSave={save} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {journals.map((j) => (
          <div key={j.slug} data-testid={`admin-journal-${j.slug}`} className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-4 flex items-start justify-between gap-3">
            <div className="min-w-0"><h4 className="font-serif font-bold text-slate-900 dark:text-slate-100 truncate">{j.title}</h4><p className="text-xs text-slate-500 mt-1">{j.abbreviation} · {j.paper_count} articles</p></div>
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="icon" onClick={() => { setEditing(j); setOpen(true); }} data-testid={`edit-journal-${j.slug}`}><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => del(j.slug)} data-testid={`delete-journal-${j.slug}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoriesTab({ categories, reload }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/categories", { name, description: desc, icon: "BookOpen" }); toast.success("Category added"); setName(""); setDesc(""); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (slug) => { if (!window.confirm("Delete category?")) return; try { await api.delete(`/categories/${slug}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div>
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-4 mb-4 flex flex-col sm:flex-row gap-2">
        <Input placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} data-testid="category-name-input" />
        <Input placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} />
        <Button onClick={add} className="rounded-full gap-1.5 shrink-0" data-testid="add-category-btn"><Plus className="w-4 h-4" /> Add</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {categories.map((c) => (
          <div key={c.slug} className="bg-card rounded-lg border border-slate-200 dark:border-slate-800 p-3 flex items-center justify-between">
            <div><span className="font-medium">{c.name}</span><span className="text-xs text-slate-400 ml-2">{c.journal_count} journals</span></div>
            <Button variant="ghost" size="icon" onClick={() => del(c.slug)} data-testid={`delete-category-${c.slug}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpecialTab({ journals }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", journal_slug: "", description: "", due_date: "" });
  const load = () => api.get("/special-issues").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.title || !f.journal_slug) { toast.error("Title and journal required."); return; }
    try { await api.post("/special-issues", f); toast.success("Special issue created"); setF({ title: "", journal_slug: "", description: "", due_date: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/special-issues/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-4 mb-4 space-y-2">
        <Input placeholder="Special issue title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="special-title-input" />
        <div className="grid sm:grid-cols-2 gap-2">
          <Select value={f.journal_slug} onValueChange={(v) => setF({ ...f, journal_slug: v })}>
            <SelectTrigger data-testid="special-journal-select"><SelectValue placeholder="Journal" /></SelectTrigger>
            <SelectContent>{journals.map((j) => <SelectItem key={j.slug} value={j.slug}>{j.title}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="special-due-input" />
        </div>
        <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} />
        <Button onClick={add} className="rounded-full gap-1.5" data-testid="add-special-btn"><Plus className="w-4 h-4" /> Create</Button>
      </div>
      <div className="space-y-2">
        {items.map((s) => (
          <div key={s.id} className="bg-card rounded-lg border border-slate-200 dark:border-slate-800 p-3 flex items-center justify-between">
            <div><span className="font-medium">{s.title}</span><span className="text-xs text-slate-400 ml-2">Due {s.due_date} · {s.journal?.title}</span></div>
            <Button variant="ghost" size="icon" onClick={() => del(s.id)} data-testid={`delete-special-${s.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ContentAdmin() {
  const [categories, setCategories] = useState([]);
  const [journals, setJournals] = useState([]);
  const loadCats = () => api.get("/categories").then(({ data }) => setCategories(data));
  useEffect(() => { loadCats(); api.get("/journals").then(({ data }) => setJournals(data)); }, []);

  return (
    <div data-testid="content-admin">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2"><BookOpen className="w-6 h-6 text-amber-600" /> Content Management</h1>
      <p className="text-slate-500 text-sm mb-5">Create and edit journals, categories and special issues.</p>
      <Tabs defaultValue="journals">
        <TabsList>
          <TabsTrigger value="journals" data-testid="content-tab-journals">Journals</TabsTrigger>
          <TabsTrigger value="categories" data-testid="content-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="special" data-testid="content-tab-special">Special Issues</TabsTrigger>
        </TabsList>
        <TabsContent value="journals" className="mt-5"><JournalsTab categories={categories} /></TabsContent>
        <TabsContent value="categories" className="mt-5"><CategoriesTab categories={categories} reload={loadCats} /></TabsContent>
        <TabsContent value="special" className="mt-5"><SpecialTab journals={journals} /></TabsContent>
      </Tabs>
    </div>
  );
}
